import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { SaveBar } from '@/components/SaveBar'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Recipient = components['schemas']['Recipient']
type Subscription = components['schemas']['Subscription']
type NotificationEvent = components['schemas']['NotificationEvent']
type NotificationDelivery = components['schemas']['NotificationDelivery']

const EVENTS: NotificationEvent[] = [
  'CREDENTIAL_FAILED',
  'CREDENTIAL_EXPIRING',
  'CREDENTIAL_SCOPES_REDUCED',
  'BASELINE_COMPLETE',
  'JOB_STARTED',
  'JOB_OUTCOME',
  'HISTORY_REWRITE_DIGEST',
  'REPO_DELETED_DETECTED',
  'ALERT_RAISED',
]
const EVENT_LABEL: Record<NotificationEvent, string> = {
  CREDENTIAL_FAILED: 'Credential failed',
  CREDENTIAL_EXPIRING: 'Credential expiring',
  CREDENTIAL_SCOPES_REDUCED: 'Credential scopes reduced',
  BASELINE_COMPLETE: 'First backup complete',
  JOB_STARTED: 'Job started',
  JOB_OUTCOME: 'Job outcome',
  HISTORY_REWRITE_DIGEST: 'History rewrite digest',
  REPO_DELETED_DETECTED: 'Repository deletion detected',
  ALERT_RAISED: 'Alert raised',
}
const RECIPIENT_STATE_LABEL: Record<string, string> = { PENDING: 'Verification pending', VERIFIED: 'Verified', DISABLED: 'Disabled' }
const RECIPIENT_STATE_CHIP: Record<string, 'pending' | 'active' | 'failure'> = { PENDING: 'pending', VERIFIED: 'active', DISABLED: 'failure' }
const DISABLED_REASON_LABEL: Record<string, string> = { HARD_BOUNCE: 'hard bounce', COMPLAINT: 'complaint', DISABLED_BY_USER: 'disabled by user' }
const OUTCOME_LABEL: Record<string, string> = { SENT: 'Sent', SUPPRESSED: 'Suppressed', BOUNCED: 'Bounced', COMPLAINED: 'Complained', FAILED: 'Failed' }
const OUTCOME_CHIP: Record<string, 'active' | 'grey' | 'failure'> = { SENT: 'active', SUPPRESSED: 'grey', BOUNCED: 'failure', COMPLAINED: 'failure', FAILED: 'failure' }
// `Prototype_v2.html` id="v-notify" — per-event code + the explanatory note under each row's label.
const EVENT_CODE: Record<NotificationEvent, string> = {
  CREDENTIAL_FAILED: 'CREDENTIAL_FAILED',
  CREDENTIAL_EXPIRING: 'CREDENTIAL_EXPIRING',
  CREDENTIAL_SCOPES_REDUCED: 'CREDENTIAL_SCOPES_REDUCED',
  BASELINE_COMPLETE: 'BASELINE_COMPLETE',
  JOB_STARTED: 'JOB_STARTED',
  JOB_OUTCOME: 'JOB_OUTCOME',
  HISTORY_REWRITE_DIGEST: 'HISTORY_REWRITE_DIGEST',
  REPO_DELETED_DETECTED: 'REPO_DELETED_DETECTED',
  ALERT_RAISED: 'ALERT_RAISED',
}
const EVENT_NOTE: Record<NotificationEvent, string> = {
  CREDENTIAL_FAILED: 'Protection has stopped. Cannot be filtered.',
  CREDENTIAL_EXPIRING: 'Five reminders: T-30, T-14, T-7, T-1, T-0.',
  CREDENTIAL_SCOPES_REDUCED: 'Backups still run, incompletely.',
  BASELINE_COMPLETE:
    'Once per workspace, by construction. Deliberately not a job event — the baseline is a job and already sends both of the two below, so a baseline sends three.',
  JOB_STARTED: "Every job emits it, whatever its type and however it was triggered. Off by default — turn it on for the job's-eye view. A start has no outcome, so there is nothing to filter.",
  JOB_OUTCOME:
    'Every job emits it — data backup, configuration backup and restore alike; the job type rides in the email. Filtered on by default: four emails per workspace per day before on-demand runs. A cancelled job is a chosen outcome and is filtered out.',
  HISTORY_REWRITE_DIGEST: 'One per workspace per run, never one per ref.',
  REPO_DELETED_DETECTED: 'It remains restorable from the last snapshot that held it.',
  ALERT_RAISED: 'The alert panel is the in-product surface; this is the out-of-product one.',
}

/**
 * `v-notify` — process/09-SCREEN-SPECS/v-notify.md. **`alertListLostCommits` is named in this
 * spec's Operations table but its path is scoped to one specific already-raised alert
 * (`/alerts/{alert_id}/lost-commits`), not a list of subscribable conditions** — it does not fit
 * "the lost-commit conditions a subscription can be set against" and is not wired here; the
 * subscription matrix instead enumerates the closed `NotificationEvent` set directly, the same way
 * a closed, corpus-stable enum is rendered everywhere else in this app. Reported, not resolved by
 * guessing. **The subscription matrix here is simplified to `ALL_WORKSPACES` scope only** — the
 * per-workspace `scope` value is real in the schema but has no picker built this pass.
 * `notification_deliveries.delivery_id` is never serialised (rule 1) and none is rendered.
 *
 * **A real, load-bearing schema asymmetry found while wiring this**: `organisationGetSubscriptions`
 * returns `Subscription[]` with no `recipient_id`, but `organisationReplaceSubscriptions`'s body
 * (`SubscriptionReplace.items[]`) *requires* one — subscriptions are per-recipient on write, but the
 * read has no way to say which recipient a returned row belongs to. This screen cannot reconstruct
 * per-recipient state from the read, so the one matrix shown is applied to every VERIFIED recipient
 * identically on save (`saveSubscriptions` fans the same event set out across all of them) — a
 * reported gap in the contract, not a UI simplification chosen freely.
 *
 * **Both chips recoloured (W172)**: the recipient-state chip was a `VERIFIED`-only blue/grey
 * binary; the prototype's own recipient list draws `Verified` as `st-active`, `Verification
 * pending` as `st-pending`, and a disabled/bounced recipient as `solid-fail`, directly matched to
 * this schema's `PENDING`/`VERIFIED`/`DISABLED`. The delivery-outcome chip has no direct prototype
 * example anywhere; `SENT`/`SUPPRESSED`/`BOUNCED`/`COMPLAINED`/`FAILED` are mapped by the same
 * success/neutral/failure semantic every other status vocabulary in this build follows, not from
 * an observed row — flagged here as inferred rather than sourced.
 *
 * **`HISTORY_REWRITE_DIGEST`'s own branch-scope checkboxes added** — `Subscription.notify_
 * protected_branches`/`notify_other_branches` (real schema fields, not client state), matching
 * `Prototype_v2.html`'s own two checkboxes for this event. Was flagged as a contract gap (no field
 * existed) and closed at the source rather than built client-side-only; see `design/
 * 20-notification-surface.md`'s own addition for the field-level reasoning.
 */
export function VNotify() {
  const orgId = useActiveOrgId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [newEmail, setNewEmail] = useState('')

  const recipients = useQuery({
    queryKey: ['recipients', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/recipients', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly Recipient[]
    },
  })

  const subscriptions = useQuery({
    queryKey: ['subscriptions', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/subscriptions', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly Subscription[]
    },
  })

  const deliveries = useQuery({
    queryKey: ['deliveries', orgId, pager.cursor],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/notifications', { params: { path: { org_id: orgId }, query: { cursor: pager.cursor } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const addRecipient = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/recipients', {
        params: { path: { org_id: orgId } },
        body: { email: newEmail, subscribe_defaults: true },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      return unwrap(response as { data?: Recipient; error?: Problem })
    },
    onSuccess: () => {
      setNewEmail('')
      queryClient.invalidateQueries({ queryKey: ['recipients', orgId] })
    },
  })

  const removeRecipient = useMutation({
    mutationFn: async (recipientId: string) => {
      const client = getApiClient()
      const { error } = await client.DELETE('/orgs/{org_id}/recipients/{recipient_id}', { params: { path: { org_id: orgId, recipient_id: recipientId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recipients', orgId] }),
  })

  const resendVerification = useMutation({
    mutationFn: async (recipientId: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/recipients/{recipient_id}/resend', { params: { path: { org_id: orgId, recipient_id: recipientId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recipients', orgId] }),
  })

  const [matrix, setMatrix] = useState<
    Record<NotificationEvent, { subscribed: boolean; cadence: 'IMMEDIATE' | 'DAILY_DIGEST'; onlyBad: boolean; notifyProtectedBranches: boolean; notifyOtherBranches: boolean }> | undefined
  >(undefined)
  const savedMatrix = Object.fromEntries(
    EVENTS.map((event) => {
      const existing = subscriptions.data?.find((s) => s.event_type === event && s.scope === 'ALL_WORKSPACES')
      return [
        event,
        {
          subscribed: Boolean(existing),
          cadence: existing?.cadence ?? 'IMMEDIATE',
          onlyBad: existing?.only_bad_outcomes ?? false,
          notifyProtectedBranches: existing?.notify_protected_branches ?? true,
          notifyOtherBranches: existing?.notify_other_branches ?? false,
        },
      ]
    }),
  ) as Record<NotificationEvent, { subscribed: boolean; cadence: 'IMMEDIATE' | 'DAILY_DIGEST'; onlyBad: boolean; notifyProtectedBranches: boolean; notifyOtherBranches: boolean }>
  const effectiveMatrix = matrix ?? savedMatrix

  const saveSubscriptions = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const verifiedRecipientIds = (recipients.data ?? []).filter((r) => r.state === 'VERIFIED').map((r) => r.recipient_id)
      const subscribedEvents = EVENTS.filter((e) => effectiveMatrix[e].subscribed)
      const items = verifiedRecipientIds.flatMap((recipient_id) =>
        subscribedEvents.map((event_type) => ({
          recipient_id,
          event_type,
          scope: 'ALL_WORKSPACES' as const,
          cadence: effectiveMatrix[event_type].cadence,
          only_bad_outcomes: effectiveMatrix[event_type].onlyBad,
          notify_protected_branches: effectiveMatrix[event_type].notifyProtectedBranches,
          notify_other_branches: effectiveMatrix[event_type].notifyOtherBranches,
        })),
      )
      const response = await client.PUT('/orgs/{org_id}/subscriptions', { params: { path: { org_id: orgId } }, body: { items } })
      return unwrap(response as { data?: unknown; error?: Problem })
    },
    onSuccess: () => {
      setMatrix(undefined)
      queryClient.invalidateQueries({ queryKey: ['subscriptions', orgId] })
    },
  })

  const recipientsProblem = recipients.error instanceof ApiProblemError ? recipients.error.problem : undefined
  const deliveriesRows = (deliveries.data?.items ?? []) as unknown as readonly NotificationDelivery[]

  return (
    <div data-scr={unitId('v-notify')}>
      <h1 className="m-0 mb-8 text-section font-semibold">Notifications</h1>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
        <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
          Recipients
        </div>
        {recipientsProblem && (isUnexpectedRefusal(recipientsProblem.refusal_kind) ? <UnexpectedRefusal problem={recipientsProblem} /> : <Note>{recipientsProblem.title}</Note>)}
        <p className="mb-5 text-body-lg">Notifications are sent by email. Add the addresses that should receive them. Every address receives the notifications selected alongside.</p>
        <div className="flex gap-3 mb-5">
          <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@example.com" className="border border-bd2 rounded-1 py-2 px-3 text-body-lg flex-1" />
          <Button data-act={actionId('recipient.create')} disabled={!newEmail || addRecipient.isPending} onClick={() => addRecipient.mutate()}>
            Add
          </Button>
        </div>
        {addRecipient.error instanceof ApiProblemError && <Note>{addRecipient.error.problem.title}</Note>}
        <div data-cmp="recipient-row" className="flex flex-col gap-3">
          {(recipients.data ?? []).map((r) => (
            <div key={r.recipient_id} className="flex items-center gap-4 py-3 border-b border-bd last:border-b-0">
              <span className="flex-1">{r.email}</span>
              <Chip variant={RECIPIENT_STATE_CHIP[r.state] ?? 'plain'}>
                {r.state === 'DISABLED' && r.disabled_reason ? `Disabled · ${DISABLED_REASON_LABEL[r.disabled_reason] ?? r.disabled_reason}` : (RECIPIENT_STATE_LABEL[r.state] ?? r.state)}
              </Chip>
              {r.state !== 'VERIFIED' && (
                <Button
                  data-act={actionId('recipient.resendVerification')}
                  ghost
                  disabled={resendVerification.isPending && resendVerification.variables === r.recipient_id}
                  onClick={() => resendVerification.mutate(r.recipient_id)}
                >
                  {r.state === 'DISABLED' ? 'Re-verify' : 'Resend'}
                </Button>
              )}
              <Button
                data-act={actionId('recipient.delete')}
                ghost
                disabled={removeRecipient.isPending && removeRecipient.variables === r.recipient_id}
                onClick={() => removeRecipient.mutate(r.recipient_id)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        <p className="mt-3 text-small text-tx2">An address receives nothing until it is verified.</p>
        <p className="mt-2 text-small text-tx2">
          A bounced or complained address is <b>disabled and shown</b> with its reason, never silently dropped — and it raises an alert of its own. A pending address stays pending
          indefinitely rather than disappearing.
        </p>
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
        <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
          Events and schedule
        </div>
        <p className="mb-4 text-body-lg">Choose which jobs send an email, how often, and whether every outcome is sent or only the ones that need attention.</p>
        <Note>Organisation-wide only this pass — subscribing one specific workspace is not built yet.</Note>
        <div className="mt-4">
          <GridTable
            columns={[
              {
                key: 'notifyFor',
                header: 'Notify for',
                render: (event) => (
                  <>
                    <b>{EVENT_LABEL[event]}</b>
                    <div className="text-small text-tx2">{EVENT_CODE[event]}</div>
                    <div className="text-small text-tx2 mt-1">{EVENT_NOTE[event]}</div>
                  </>
                ),
              },
              {
                key: 'subscribed',
                header: 'Subscribed',
                render: (event) => (
                  <input
                    type="checkbox"
                    checked={effectiveMatrix[event].subscribed}
                    onChange={(e) => setMatrix({ ...effectiveMatrix, [event]: { ...effectiveMatrix[event], subscribed: e.target.checked } })}
                  />
                ),
              },
              {
                key: 'when',
                header: 'When',
                render: (event) => (
                  <select
                    value={effectiveMatrix[event].cadence}
                    disabled={!effectiveMatrix[event].subscribed}
                    onChange={(e) => setMatrix({ ...effectiveMatrix, [event]: { ...effectiveMatrix[event], cadence: e.target.value as 'IMMEDIATE' | 'DAILY_DIGEST' } })}
                    className="border border-bd2 rounded-1 py-1 px-2"
                  >
                    <option value="IMMEDIATE">Immediate</option>
                    <option value="DAILY_DIGEST">Daily digest</option>
                  </select>
                ),
              },
              {
                key: 'send',
                header: 'Send',
                render: (event) =>
                  event === 'JOB_OUTCOME' ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!effectiveMatrix[event].subscribed}
                        checked={effectiveMatrix[event].onlyBad}
                        onChange={(e) => setMatrix({ ...effectiveMatrix, [event]: { ...effectiveMatrix[event], onlyBad: e.target.checked } })}
                      />
                      Partial or failed only
                    </label>
                  ) : event === 'HISTORY_REWRITE_DIGEST' ? (
                    <div className="flex flex-col gap-1">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          disabled={!effectiveMatrix[event].subscribed}
                          checked={effectiveMatrix[event].notifyProtectedBranches}
                          onChange={(e) => setMatrix({ ...effectiveMatrix, [event]: { ...effectiveMatrix[event], notifyProtectedBranches: e.target.checked } })}
                        />
                        Protected branches — on by default
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          disabled={!effectiveMatrix[event].subscribed}
                          checked={effectiveMatrix[event].notifyOtherBranches}
                          onChange={(e) => setMatrix({ ...effectiveMatrix, [event]: { ...effectiveMatrix[event], notifyOtherBranches: e.target.checked } })}
                        />
                        All other branches — off by default
                      </label>
                    </div>
                  ) : (
                    <span>Every occurrence</span>
                  ),
              },
            ]}
            rows={EVENTS}
            getRowKey={(event) => event}
          />
        </div>
        <p className="mt-3 text-small text-tx2">
          <b>Nine subscribable events.</b> Three more are defined and are never offered here: two left with the paused restore, and restore completion now flows through{' '}
          <span className="font-mono">JOB_OUTCOME</span> like every other job rather than sending a differently-named email of its own. The third reports a feature that is a future
          enhancement. Two cadences only: immediate, or a daily digest. A row is a subscription; no row means unsubscribed.
        </p>
        <p className="mt-2 text-small text-tx2">
          Recipients are <b>verified addresses, not roles</b>. A set derived from people goes quiet when a person leaves — and what would go quiet here is the mechanism that tells you
          things have gone quiet.
        </p>
        <SaveBar
          dataAct={actionId('subscription.replace')}
          dirty={Boolean(matrix)}
          pending={saveSubscriptions.isPending}
          onSave={() => saveSubscriptions.mutate()}
          onCancel={() => setMatrix(undefined)}
        />
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
        <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
          Delivery history
        </div>
        <GridTable
          columns={[
            { key: 'event', header: 'Event', render: (d) => EVENT_LABEL[d.event_type] ?? d.event_type },
            { key: 'recipient', header: 'Recipient', render: (d) => d.recipient_email },
            { key: 'outcome', header: 'Outcome', render: (d) => <Chip variant={OUTCOME_CHIP[d.outcome] ?? 'plain'}>{OUTCOME_LABEL[d.outcome] ?? d.outcome}</Chip> },
            { key: 'sent', header: 'Sent', render: (d) => d.sent_at },
          ]}
          rows={deliveriesRows}
          getRowKey={(d) => `${d.event_type}-${d.recipient_email}-${d.sent_at}`}
        />
        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between mt-5">
          <Note>Paged by cursor only — no total is served for this list.</Note>
          <Pager
            hasPrev={pager.hasPrev}
            hasNext={Boolean(deliveries.data?.next_cursor)}
            loading={deliveries.isFetching}
            onPrev={pager.goPrev}
            onNext={() => {
              if (deliveries.data?.next_cursor) pager.goNext(deliveries.data.next_cursor)
            }}
          />
        </div>
      </div>
    </div>
  )
}
